import React, { useEffect, useState } from "react";
import api from "../api";
import { Link } from "react-router-dom";
import { hasPermission } from "../utils/auth";

export default function ContractsListPage({ user }) {
  const [contracts, setContracts] = useState([]);
  const [search, setSearch] = useState("");
  const [processingId, setProcessingId] = useState(null);
  const [message, setMessage] = useState("");
  const [deletingId, setDeletingId] = useState(null);
  const [highlightModal, setHighlightModal] = useState({
    open: false,
    contract: null,
  });
  const [accessModal, setAccessModal] = useState({
    open: false,
    contract: null,
    reason: "",
    status: "NOT_REQUESTED",
    decisionReason: "",
  });
  const [accessSubmitting, setAccessSubmitting] = useState(false);
  const [highlights, setHighlights] = useState([]);
  const [highlightsLoading, setHighlightsLoading] = useState(false);
  const [highlightsError, setHighlightsError] = useState("");
  const [refreshingHighlights, setRefreshingHighlights] = useState(false);
  const [contractTypes, setContractTypes] = useState([]);
  const [typeFilter, setTypeFilter] = useState("");
  const [editModal, setEditModal] = useState({ open: false, contract: null });
  const [editForm, setEditForm] = useState({
    title: "",
    contract_type_id: "",
    duration_months: "",
    gross_amount: "",
    net_amount: "",
  });
  const [editSubmitting, setEditSubmitting] = useState(false);
  const [editError, setEditError] = useState("");
  const canRead = hasPermission(user, "contracts:read");
  const canReadTypes = hasPermission(user, "contract-types:read");
  const canCreate = hasPermission(user, "contracts:create");
  const canUpdate = hasPermission(user, "contracts:update");
  const canDelete = hasPermission(user, "contracts:delete");
  const canProcess = hasPermission(user, "contracts:process");
  const canChat = hasPermission(user, "chat:use");
  const canRequestAccess = hasPermission(user, "access-requests:request");
  const canRefreshHighlights = hasPermission(user, "highlights:refresh");

  // In production the API is same-domain (baseURL is the relative "/api"),
  // so file links must stay relative too, resolving against whatever host
  // is actually serving the page. Only fall back to an absolute dev URL
  // when baseURL is itself an absolute URL (local dev, different origin).
  const rawApiBase = api.defaults.baseURL || "/api";
  const backendBaseUrl = /^https?:\/\//i.test(rawApiBase)
    ? rawApiBase.replace(/\/api$/, "")
    : "";

  const fetchContracts = async () => {
    if (!canRead) {
      setMessage("You do not have permission to view contracts.");
      return;
    }
    try {
      const res = await api.get("/contracts");
      setContracts(res.data);
    } catch (err) {
      console.error("Error fetching contracts:", err);
    }
  };

  const fetchContractTypes = async () => {
    if (!canReadTypes) return;
    try {
      const res = await api.get("/contract-types");
      setContractTypes(res.data);
    } catch (err) {
      console.error("Error fetching contract types:", err);
    }
  };

  useEffect(() => {
    fetchContracts();
    fetchContractTypes();
  }, [canRead, canReadTypes]);

  const selectedTypeName =
    contractTypes.find((t) => String(t.id) === typeFilter)?.name?.toLowerCase() ||
    "";

  const filtered = contracts.filter((c) => {
    const q = search.toLowerCase();
    const matchesSearch =
      c.title.toLowerCase().includes(q) ||
      (c.contract_type_name || "").toLowerCase().includes(q) ||
      (c.description || "").toLowerCase().includes(q);
    const matchesType =
      !typeFilter ||
      String(c.contract_type_id || "") === typeFilter ||
      (c.contract_type_name || "").toLowerCase() === selectedTypeName;

    return matchesSearch && matchesType;
  });

  const openEditModal = (contract) => {
    setEditModal({ open: true, contract });
    setEditForm({
      title: contract.title || "",
      contract_type_id: contract.contract_type_id ? String(contract.contract_type_id) : "",
      duration_months:
        contract.duration_months !== null && contract.duration_months !== undefined
          ? String(contract.duration_months)
          : "",
      gross_amount:
        contract.gross_amount !== null && contract.gross_amount !== undefined
          ? String(contract.gross_amount)
          : "",
      net_amount:
        contract.net_amount !== null && contract.net_amount !== undefined
          ? String(contract.net_amount)
          : "",
    });
    setEditError("");
  };

  const closeEditModal = () => {
    setEditModal({ open: false, contract: null });
    setEditForm({
      title: "",
      contract_type_id: "",
      duration_months: "",
      gross_amount: "",
      net_amount: "",
    });
    setEditError("");
  };

  const handleEditChange = (field, value) => {
    setEditForm((prev) => ({ ...prev, [field]: value }));
  };

  const submitEdit = async () => {
    if (!editModal.contract) return;
    const payload = {
      title: editForm.title.trim(),
      contract_type_id: editForm.contract_type_id ? Number(editForm.contract_type_id) : null,
      duration_months:
        editForm.duration_months === "" ? null : Number(editForm.duration_months),
      gross_amount: editForm.gross_amount === "" ? null : Number(editForm.gross_amount),
      net_amount: editForm.net_amount === "" ? null : Number(editForm.net_amount),
    };

    if (!payload.title) {
      setEditError("Title is required.");
      return;
    }
    if (
      editForm.duration_months !== "" &&
      (Number.isNaN(payload.duration_months) || payload.duration_months < 0)
    ) {
      setEditError("Duration must be a positive number or blank.");
      return;
    }
    if (
      editForm.gross_amount !== "" &&
      Number.isNaN(payload.gross_amount)
    ) {
      setEditError("Gross amount must be a number or blank.");
      return;
    }
    if (
      editForm.net_amount !== "" &&
      Number.isNaN(payload.net_amount)
    ) {
      setEditError("Net amount must be a number or blank.");
      return;
    }

    try {
      setEditSubmitting(true);
      setEditError("");
      await api.put(`/contracts/${editModal.contract.id}`, payload);
      setMessage("Contract updated successfully.");
      await fetchContracts();
      closeEditModal();
    } catch (err) {
      console.error("Update contract error:", err);
      setEditError(
        err.response?.data?.error || "Failed to update contract."
      );
    } finally {
      setEditSubmitting(false);
    }
  };

  const handleProcess = async (id) => {
    if (!canProcess) {
      setMessage("You do not have permission to process contracts.");
      return;
    }
    try {
      setMessage("");
      setProcessingId(id);
      const res = await api.post(`/contracts/${id}/process`);
      setMessage(`Contract ${id}: ${res.data.message || "Processing started"}`);
      await fetchContracts();
    } catch (err) {
      console.error("Process error:", err);
      setMessage(
        err.response?.data?.error || "Failed to process contract. Check console."
      );
    } finally {
      setProcessingId(null);
    }
  };

  const openHighlights = async (contract) => {
    const accessStatus = (contract.access_status || "").toUpperCase();
    if (user?.role !== "CEO" && accessStatus !== "APPROVED") {
      setAccessModal({
        open: true,
        contract,
        reason: "",
        status: contract.access_status || "NOT_REQUESTED",
        decisionReason: contract.access_decision_reason || "",
      });
      return;
    }
    if (contract.status !== "READY") {
      setMessage("This contract is not ready yet. Finish processing first.");
      return;
    }
    setHighlightModal({ open: true, contract });
    setHighlights([]);
    setHighlightsError("");
    setHighlightsLoading(true);
    try {
      const res = await api.get(`/contracts/${contract.id}/highlights`);
      setHighlights(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("Highlights error:", err);
      setHighlightsError(
        err.response?.data?.error || "Failed to load highlights."
      );
    } finally {
      setHighlightsLoading(false);
    }
  };

  const closeHighlights = () => {
    setHighlightModal({ open: false, contract: null });
    setHighlights([]);
    setHighlightsError("");
  };

  const refreshHighlights = async () => {
    if (!canRefreshHighlights) {
      setHighlightsError("You do not have permission to refresh highlights.");
      return;
    }
    if (!highlightModal.contract) return;
    setRefreshingHighlights(true);
    setHighlightsError("");
    try {
      const res = await api.post(
        `/contracts/${highlightModal.contract.id}/highlights/refresh`
      );
      setHighlights(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("Refresh highlights error:", err);
      setHighlightsError(
        err.response?.data?.error || "Failed to refresh highlights."
      );
    } finally {
      setRefreshingHighlights(false);
    }
  };

  const requestAccess = async () => {
    if (!accessModal.contract) return;
    if (!canRequestAccess) {
      setAccessModal((prev) => ({
        ...prev,
        status: "FORBIDDEN",
        decisionReason: "You do not have permission to request access.",
      }));
      return;
    }
    try {
      setAccessSubmitting(true);
      const res = await api.post(
        `/contracts/${accessModal.contract.id}/request-access`,
        { reason: accessModal.reason }
      );
      setMessage("Access request submitted.");
      setAccessModal((prev) => ({
        ...prev,
        status: res.data.status || "PENDING",
      }));
      await fetchContracts();
    } catch (err) {
      console.error("Request access error:", err);
      setMessage(
        err.response?.data?.error || "Failed to submit access request."
      );
    } finally {
      setAccessSubmitting(false);
    }
  };

  const accessBadge = (status) => {
    const base = "inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-semibold border";
    if (status === "APPROVED")
      return <span className={`${base} bg-green-50 text-green-700 border-green-200`}>Access: Approved</span>;
    if (status === "PENDING")
      return <span className={`${base} bg-amber-50 text-amber-700 border-amber-200`}>Access: Pending</span>;
    if (status === "REJECTED")
      return <span className={`${base} bg-red-50 text-red-600 border-red-200`}>Access: Rejected</span>;
    return <span className={`${base} bg-slate-50 text-slate-600 border-slate-200`}>Access: Request</span>;
  };

  const renderStatusBadge = (status) => {
    const base = "inline-flex items-center px-3 py-1 rounded-full text-xs font-medium";
    if (status === "READY")
      return <span className={`${base} bg-green-50 text-green-700 border border-green-200`}>READY</span>;
    if (status === "PROCESSING")
      return <span className={`${base} bg-amber-50 text-amber-700 border border-amber-200`}>PROCESSING</span>;
    if (status === "FAILED")
      return <span className={`${base} bg-red-50 text-red-700 border border-red-200`}>FAILED</span>;
    return <span className={`${base} bg-slate-50 text-slate-600 border border-slate-200`}>{status || "UPLOADED"}</span>;
  };

  const handleDelete = async (id) => {
    const confirmed = window.confirm(
      "Are you sure you want to delete this contract? All related data will be removed."
    );
    if (!confirmed) return;

    try {
      setMessage("");
      setDeletingId(id);
      await api.delete(`/contracts/${id}`);
      setMessage("Contract deleted successfully.");
      await fetchContracts();
    } catch (err) {
      console.error("Delete error:", err);
      setMessage(
        err.response?.data?.error ||
          "Failed to delete contract. Check console."
      );
    } finally {
      setDeletingId(null);
    }
  };

  if (!canRead) {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="rounded-lg border border-amber-200 bg-amber-50 text-amber-800 px-4 py-3">
          You do not have permission to view contracts.
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="w-full space-y-6">
      {/* Header row */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-navy">Contracts</h1>
          <p className="text-sm text-slate-600 mt-1">
            Overview of all uploaded and AI-ready contracts.
          </p>
        </div>

        {canCreate && (
          <Link
            to="/contracts/new"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-navy text-white text-sm font-semibold shadow-sm hover:bg-navy/90"
          >
            <span>＋</span>
            <span>Upload Contract</span>
          </Link>
        )}
      </div>

      {message && (
        <div className="rounded-lg bg-amber-50 border border-amber-300 px-4 py-2 text-sm text-amber-900">
          {message}
        </div>
      )}

      {/* Search + filters */}
      <div className="flex flex-col md:flex-row md:items-center gap-3">
        <div className="relative flex-1">
          <span className="absolute left-3 top-2.5 text-slate-400 text-sm">
            🔍
          </span>
          <input
            className="w-full pl-8 pr-3 py-2 rounded-lg border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-gold/70 focus:border-gold/70"
            placeholder="Search by title, type, or description..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <div className="flex items-center gap-2">
          <label className="text-xs font-semibold text-slate-600 uppercase tracking-wide">
            Type
          </label>
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="min-w-[180px] px-3 py-2 rounded-lg border border-slate-200 bg-white text-xs text-slate-700 focus:outline-none focus:ring-2 focus:ring-gold/70 focus:border-gold/70"
            disabled={!canReadTypes}
          >
            <option value="">All Types</option>
            {contractTypes.map((t) => (
              <option key={t.id} value={String(t.id)}>
                {t.name}
              </option>
            ))}
          </select>
          {typeFilter && (
            <button
              type="button"
              onClick={() => setTypeFilter("")}
              className="px-3 py-2 rounded-lg border border-slate-200 bg-white text-xs text-slate-700 hover:bg-slate-50"
            >
              Clear
            </button>
          )}
        </div>
      </div>

      {/* Table card */}
      <div className="bg-white shadow-sm rounded-xl overflow-hidden border border-slate-200">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="bg-navy text-white text-left">
                <th className="px-4 py-3 font-semibold">ID</th>
                <th className="px-4 py-3 font-semibold">Title</th>
                <th className="px-4 py-3 font-semibold">Type</th>
                <th className="px-4 py-3 font-semibold">Contract Duration</th>
                <th className="px-4 py-3 font-semibold">Gross</th>
                <th className="px-4 py-3 font-semibold">Net</th>
                <th className="px-4 py-3 font-semibold">Contract</th>
                <th className="px-4 py-3 font-semibold">Access</th>
                <th className="px-4 py-3 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((c, idx) => (
                <tr
                  key={c.id}
                  className={`${idx % 2 === 0 ? "bg-white" : "bg-slate-50/60"} hover:bg-amber-50/60 cursor-pointer`}
                  role="button"
                  tabIndex={0}
                  onClick={() => openHighlights(c)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      openHighlights(c);
                    }
                  }}
                >
                  <td className="px-4 py-2 border-t border-slate-100">
                    {c.id}
                  </td>
                  <td className="px-4 py-2 border-t border-slate-100">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        openHighlights(c);
                      }}
                      className="font-medium text-navy hover:underline text-left"
                    >
                      {c.title}
                    </button>
                    {c.description && (
                      <div className="text-xs text-slate-500 truncate max-w-xs">
                        {c.description}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-2 border-t border-slate-100">
                    {c.contract_type_name || "-"}
                  </td>
                  <td className="px-4 py-2 border-t border-slate-100">
                    {c.duration_months
                      ? `${c.duration_months} Months`
                      : c.start_date || c.end_date
                      ? "Custom"
                      : "-"}
                  </td>
                  <td className="px-4 py-2 border-t border-slate-100">
                    {c.gross_amount != null ? (
                      <span>
                        €{" "}
                        {Number(c.gross_amount).toLocaleString("de-DE")}
                      </span>
                    ) : (
                      "-"
                    )}
                  </td>
                  <td className="px-4 py-2 border-t border-slate-100">
                    {c.net_amount != null ? (
                      <span>
                        €{" "}
                        {Number(c.net_amount).toLocaleString("de-DE")}
                      </span>
                    ) : (
                      "-"
                    )}
                  </td>
                 
                  <td className="px-4 py-2 border-t border-slate-100">
                    {renderStatusBadge(c.status)}
                  </td>
                  <td className="px-4 py-2 border-t border-slate-100">
                    {accessBadge(c.access_status)}
                  </td>
                  <td className="px-4 py-2 border-t border-slate-100">
                    <div className="flex flex-wrap gap-2">
                      {c.file_path ? (
                        <a
          href={`${backendBaseUrl}/${c.file_path}`}
          target="_blank"
          rel="noreferrer"
          onClick={(e) => {
            e.stopPropagation();
            const accessStatus = (c.access_status || "").toUpperCase();
            if (user?.role !== "CEO" && accessStatus !== "APPROVED") {
              e.preventDefault();
              setAccessModal({
                open: true,
                contract: c,
                reason: "",
                status: c.access_status || "NOT_REQUESTED",
                decisionReason: c.access_decision_reason || "",
              });
            }
          }}
                          className={`px-3 py-1.5 rounded-lg border text-xs font-medium ${
                            user?.role === "CEO" || c.access_status === "APPROVED"
                              ? "border-slate-300 text-slate-700 hover:bg-slate-50"
                              : "border-slate-200 text-slate-400 cursor-not-allowed"
                          }`}
                    >
                      View PDF
                    </a>
                  ) : (
                    <button
                      type="button"
                      disabled
                      onClick={(e) => e.stopPropagation()}
                      className="px-3 py-1.5 rounded-lg border border-slate-200 text-xs font-medium text-slate-500 bg-slate-100 cursor-not-allowed"
                    >
                      View PDF
                    </button>
                  )}
                  <Link
                    to={
                      c.status === "READY"
                        ? `/contracts/${c.id}/chat`
                        : "#"
                    }
                    className={`px-3 py-1.5 rounded-lg text-xs font-semibold shadow-sm ${
                      c.status === "READY" && canChat
                        ? "bg-gold text-white hover:bg-gold/90"
                        : "bg-slate-200 text-slate-500 cursor-not-allowed"
                    }`}
                    onClick={(e) => {
                      if (c.status !== "READY") {
                        e.preventDefault();
                        setMessage(
                          "Process the PDF before starting AI chat."
                        );
                        e.stopPropagation();
                        return;
                      }
                      if (!canChat) {
                        e.preventDefault();
                        e.stopPropagation();
                        return;
                      }
                      // Allow navigation; ContractChatPage will enforce access and show request UI.
                      e.stopPropagation();
                    }}
                  >
                    AI Chat
                  </Link>
                      {canUpdate && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            openEditModal(c);
                          }}
                          className="px-3 py-1.5 rounded-lg border border-blue-200 text-blue-700 text-xs font-semibold hover:bg-blue-50"
                        >
                          Edit
                        </button>
                      )}
                      {canDelete && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(c.id);
                          }}
                          disabled={deletingId === c.id}
                          className="px-3 py-1.5 rounded-lg border border-red-200 text-red-600 text-xs font-semibold hover:bg-red-50 disabled:opacity-60"
                        >
                          {deletingId === c.id ? "Deleting…" : "Delete"}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}

              {filtered.length === 0 && (
                <tr>
                  <td
                    colSpan="9"
                    className="px-4 py-6 text-center text-sm text-slate-500"
                  >
                    No contracts found. Upload a new contract to get started.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      </div>

      {/* Edit Contract Modal */}
      {editModal.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4 py-10">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg border border-slate-200">
            <div className="flex items-start justify-between p-5 border-b border-slate-200">
              <div>
                <h3 className="text-lg font-semibold text-navy">Edit Contract</h3>
                <p className="text-sm text-slate-600">
                  {editModal.contract?.title}
                </p>
              </div>
              <button
                type="button"
                onClick={closeEditModal}
                className="text-slate-500 hover:text-slate-800 text-lg"
              >
                ×
              </button>
            </div>
            <div className="p-5 space-y-4">
              {editError && (
                <div className="rounded-lg border border-red-200 bg-red-50 text-red-700 text-sm px-3 py-2">
                  {editError}
                </div>
              )}
              <div className="space-y-1">
                <label className="text-sm font-semibold text-slate-800">
                  Title
                </label>
                <input
                  className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gold/70 focus:border-gold/70"
                  value={editForm.title}
                  onChange={(e) => handleEditChange("title", e.target.value)}
                />
              </div>
              <div className="space-y-1">
                <label className="text-sm font-semibold text-slate-800">
                  Contract Type
                </label>
                <select
                  className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gold/70 focus:border-gold/70"
                  value={editForm.contract_type_id}
                  onChange={(e) => handleEditChange("contract_type_id", e.target.value)}
                >
                  <option value="">No type</option>
                  {contractTypes.map((t) => (
                    <option key={t.id} value={String(t.id)}>
                      {t.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-1">
                  <label className="text-sm font-semibold text-slate-800">
                    Duration (months)
                  </label>
                  <input
                    type="number"
                    min="0"
                    className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gold/70 focus:border-gold/70"
                    value={editForm.duration_months}
                    onChange={(e) => handleEditChange("duration_months", e.target.value)}
                    placeholder="Leave blank if not set"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-sm font-semibold text-slate-800">
                    Gross Amount
                  </label>
                  <input
                    type="number"
                    className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gold/70 focus:border-gold/70"
                    value={editForm.gross_amount}
                    onChange={(e) => handleEditChange("gross_amount", e.target.value)}
                    placeholder="€"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-sm font-semibold text-slate-800">
                    Net Amount
                  </label>
                  <input
                    type="number"
                    className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gold/70 focus:border-gold/70"
                    value={editForm.net_amount}
                    onChange={(e) => handleEditChange("net_amount", e.target.value)}
                    placeholder="€"
                  />
                </div>
              </div>
              <div className="flex items-center justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={closeEditModal}
                  className="px-4 py-2 rounded-lg border border-slate-200 text-sm text-slate-700 hover:bg-slate-50"
                  disabled={editSubmitting}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={submitEdit}
                  disabled={editSubmitting}
                  className="px-4 py-2 rounded-lg bg-navy text-white text-sm font-semibold shadow-sm hover:bg-navy/90 disabled:opacity-60"
                >
                  {editSubmitting ? "Saving…" : "Save Changes"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Highlights Modal */}
      {highlightModal.open && (
        <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 px-4 py-10">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-3xl border border-slate-200">
            <div className="flex items-start justify-between gap-4 p-5 border-b border-slate-200">
            <div>
              <h2 className="text-xl font-semibold text-navy">
                Key Points
              </h2>
              <p className="text-sm text-slate-500">
                {highlightModal.contract?.title}
              </p>
            </div>
            <div className="flex items-center gap-3">
              {canRefreshHighlights && (
                <button
                  type="button"
                  onClick={refreshHighlights}
                  disabled={refreshingHighlights}
                  className="text-xs font-semibold text-navy border border-navy/30 px-3 py-1.5 rounded-lg hover:bg-navy/5 disabled:opacity-60"
                >
                  {refreshingHighlights ? "Refreshing…" : "Refresh"}
                </button>
              )}
              <button
                type="button"
                onClick={closeHighlights}
                className="text-slate-500 hover:text-slate-800 text-lg"
              >
                ×
              </button>
            </div>
          </div>

            <div className="p-5 space-y-3 max-h-[70vh] overflow-y-auto">
              {highlightsLoading && (
                <div className="text-sm text-slate-500">Loading…</div>
              )}
              {highlightsError && (
                <div className="text-sm text-red-600">
                  {highlightsError}
                </div>
              )}
              {!highlightsLoading &&
                !highlightsError &&
                highlights.length === 0 && (
                  <div className="text-sm text-slate-500">
                    No highlights available.
                  </div>
                )}
              {highlights.map((h, idx) => {
                const pdfLink = highlightModal.contract?.file_path
                  ? `${backendBaseUrl}/${highlightModal.contract.file_path}#page=${
                      h.page || 1
                    }`
                  : null;
                return (
                  <div
                    key={h.id || idx}
                    className="border border-slate-200 rounded-xl p-4 bg-slate-50/60"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="text-sm font-semibold text-slate-900">
                          {h.title}
                        </div>
                        <div className="text-xs text-slate-600 mt-1 leading-relaxed">
                          {h.summary}
                        </div>
                      </div>
                      {pdfLink && (
                        <a
                          href={pdfLink}
                          target="_blank"
                          rel="noreferrer"
                          className="text-xs font-semibold text-navy hover:underline whitespace-nowrap"
                        >
                          View in PDF →
                        </a>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Access Request Modal */}
      {accessModal.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4 py-10">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md border border-slate-200">
            <div className="flex items-start justify-between p-5 border-b border-slate-200">
              <div>
                <h3 className="text-lg font-semibold text-navy">
                  Request Access
                </h3>
                <p className="text-sm text-slate-600">
                  {accessModal.contract?.title}
                </p>
                {accessModal.status === "REJECTED" && accessModal.decisionReason && (
                  <p className="text-xs text-red-600 mt-1">
                    CEO note: {accessModal.decisionReason}
                  </p>
                )}
              </div>
              <button
                type="button"
                onClick={() =>
                  setAccessModal({ open: false, contract: null, reason: "", status: "NOT_REQUESTED" })
                }
                className="text-slate-500 hover:text-slate-800 text-lg"
              >
                ×
              </button>
            </div>
            <div className="p-5 space-y-3">
              <div className="text-sm">
                {accessBadge(accessModal.status)}
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-800 mb-1">
                  Reason (optional)
                </label>
                <textarea
                  className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gold/70 focus:border-gold/70"
                  rows={3}
                  placeholder="Why do you need access to this contract?"
                  value={accessModal.reason}
                  onChange={(e) =>
                    setAccessModal((prev) => ({ ...prev, reason: e.target.value }))
                  }
                />
              </div>
              <div className="flex items-center justify-between">
                <div className="text-xs text-slate-500">
                  {canRequestAccess
                    ? "Your request will be sent to the CEO for approval."
                    : "You do not have permission to request access."}
                </div>
                <button
                  type="button"
                  onClick={requestAccess}
                  disabled={accessSubmitting || !canRequestAccess}
                  className="px-4 py-2 rounded-lg bg-navy text-white text-sm font-semibold shadow-sm hover:bg-navy/90 disabled:opacity-60"
                >
                  {accessSubmitting ? "Submitting…" : "Send Request"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
